import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import {
  CloudEnvironment,
  CreateDevOpsActionDialogData,
  DevOpsActionAndParameter,
  ErmDevOpsApiClientService
} from 'erm-sdk';
import * as _ from 'lodash';
import { UpdateEnvironmentComponent } from '../update-environment/update-environment.component';
import { CreateDevopsActionDialogComponent } from '../create-devops-action/dialog/create-devops-action-dialog.component';
import dayjs from 'dayjs';

@Component({
  standalone: false,
  templateUrl: './environment-list.component.html',
  styleUrls: ['./environment-list.component.scss']
})
export class EnvironmentListComponent implements OnInit {

  isLoading = false;
  gridOptions: GridOptions;
  gridApi?: GridApi;
  selectedEnvironment?: CloudEnvironment;
  environments?: CloudEnvironment[];

  selectedDevOpsAction?: DevOpsActionAndParameter;
  devOpsActions?: DevOpsActionAndParameter[];
  createDevOpsActionDialogData?: CreateDevOpsActionDialogData;

  @ViewChild('agGrid') agGrid!: AgGridAngular;

  constructor(private ermDevOpsApiClient: ErmDevOpsApiClientService, private router: Router, private matdialog: MatDialog) {

    this.gridOptions = {
      columnDefs: this.createColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedEnvironment = _.first(event.api.getSelectedNodes().map(x => x.data));
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
      }
    };
  }

  async ngOnInit(): Promise<void> {
    try {

      this.isLoading = true;

      // Get environments
      const getEnvironmentsResponse = await this.ermDevOpsApiClient.getCloudEnvironments();
      this.environments = getEnvironmentsResponse.body?.cloudEnvironments || new Array<CloudEnvironment>();

      // Get DevOps Actions
      const getDevopsActionsAndParameter = await this.ermDevOpsApiClient.getDevOpsActionsAndParameters();
      this.devOpsActions = getDevopsActionsAndParameter.body?.devOpsActionsAndParameters || new Array<DevOpsActionAndParameter>();

      if (this.devOpsActions) {
        this.devOpsActions = this.devOpsActions.filter(x => x.actionScope == 'Environment');
      }

      // Initialize grid
      this.gridApi?.setGridOption('rowData', this.environments);

    } finally {
      this.isLoading = false;
    }
  }

  private createColumnDefs(): ColDef[] {
    const router = this.router;
    return [
      {
        headerName: 'Production',
        field: 'isProduction',
        cellStyle: (params: any) => {
          if (params.value === true) {
            return {
              backgroundColor: 'yellow'
            };
          } else {
            return null;
          }
        },
        width: 140
      },
      {
        headerName: 'Customer Id',
        field: 'customerId',
        cellRenderer: (params: any) => {
          const url = router.createUrlTree(['erm/customers', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
        width: 140
      },
      {
        headerName: 'Account Id',
        field: 'accountId',
        cellRenderer: (params: any) => {
          const url = router.createUrlTree(['erm/accounts', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
        width: 140
      },
      {
        headerName: 'Environment Id',
        field: 'environmentId',
        cellRenderer: (params: any): string => {
          const url = router.createUrlTree(['erm/environments', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
        width: 140
      },
      { headerName: 'Region', field: 'region', width: 140 },
      { headerName: 'Schema', field: 'schemaName' },
      { headerName: 'Deployment Type', field: 'deploymentType', width: 150 },
      { headerName: 'Version', field: 'version', width: 250 },
      { headerName: 'Name', field: 'name', width: 250 },
      { headerName: 'Description', field: 'description', width: 300 },
      { headerName: 'Maintenance', field: 'inMaintenanceMode' },
      { headerName: 'Available', field: 'isAvailable' },
      { headerName: 'Enabled', field: 'isEnabled' },
      {
        headerName: 'Created',
        field: 'creationTime',
        valueFormatter: (params: any) => {
          return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
        },
      },
      { headerName: 'Status', field: 'statusMessage' },
      { headerName: 'Connect Account Id', field: 'connectAccountId', width: 300 },
      { headerName: 'EMS Port', field: 'emsServicePort' }
    ];
  }

  openUpdateEnvironment(): void {
    try {
      this.isLoading = true;

      const updateEnvironmentDialogRef = this.matdialog.open(UpdateEnvironmentComponent, { data: this.selectedEnvironment });

      updateEnvironmentDialogRef.afterClosed().subscribe(async dialogResult => {
        const result = dialogResult;
        if (result && result.data) {
          // update selected environment grid data
          this.gridOptions['api']?.applyTransaction({ update: [result.data as CloudEnvironment] });
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  canUpdateEnvironment(): boolean {
    return !this.selectedEnvironment;
  }

  async deleteEnvironment(): Promise<void> {
    try {
      this.isLoading = true;

      const environmentId = this.selectedEnvironment?.environmentId as string;
      await this.ermDevOpsApiClient.deleteCloudEnvironment(environmentId);
      // remove selected item from environment grid
      this.gridOptions['api']?.applyTransaction({
        remove: [this.selectedEnvironment]
      });
      this.selectedEnvironment = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  canDeleteEnvironment(): boolean {
    return !this.selectedEnvironment;
  }

  async openCreateDevOpsAction(): Promise<void> {
    try {
      this.isLoading = true;
      this.matdialog.open(CreateDevopsActionDialogComponent, {
        data: {
          selectedEnvironmentId: this.selectedEnvironment?.environmentId,
          cloudEnvironments: this.environments,
          devOpsActionsAndParameters: this.devOpsActions,
          selectedDevOpsActionAndParameter: this.selectedDevOpsAction
        } as CreateDevOpsActionDialogData
      });

    } finally {
      this.isLoading = false;
    }
  }

  canCreateDevOpsAction(): boolean {
    return this.isLoading || !this.selectedEnvironment || !this.selectedDevOpsAction;
  }
}
